import type { SlotSequence } from './types';

/**
 * CACHE DA SEQUÊNCIA DE SLOTS — tira o round-trip de sync do caminho de playback no boot.
 *
 * <p>O PROBLEMA MEDIDO (QB50B, Tizen 6.5, 2026-07-28). A `slot_sequence` só existia em memória:
 * vinha de uma resposta de sync e nada a persistia. Então **todo** boot — inclusive cada reload do
 * refresh periódico — precisava completar uma ida à rede antes de o player saber o que tocar.
 * Orçamento do preto medido em 8 boots: `react` em 1006ms, `playlist` em **2289ms**. Ou seja
 * **1283ms** de tela apagada esperando a rede, num aparelho cuja mídia já está toda em disco.
 *
 * <p>Também fecha metade do mandato offline-first do Renato (`.claude/rules/cache-offline-first.md`,
 * R3): "ao ligar sem internet, o player deve restaurar a última programação do disco e voltar a
 * tocar — sem depender de um sync bem-sucedido no boot". A mídia já era cacheada; a PROGRAMAÇÃO
 * não. Uma TV que religava sem internet não sabia o que tocar.
 *
 * <p>A GUARDA: idade. Até out/2026 só restaurávamos na MESMA janela horária, porque o
 * `scheduledOffsetS` de cada slot é relativo ao `hourWindowStart`. Mas a posição no loop deixou
 * de vir dali: vem da ÉPOCA canônica (`playback_started_at`), gravada junto, e em grupo exclusivo
 * o servidor gera a mesma sequência hora após hora. O `scheduledOffsetS` hoje só alimenta
 * telemetria. Então uma sequência de ontem continua certa — e um player que religa depois de uma
 * noite desligado já sobe em fase com o grupo, em vez de tocar a lista legada e saltar quando o
 * sync chega. O que mudou de conteúdo nesse meio-tempo o sync corrige logo atrás, como sempre.
 *
 * <p>NÃO substitui o sync: é um atalho para o PRIMEIRO frame. O sync roda logo atrás e a resposta
 * dele sobrepõe a sequência restaurada pelo caminho normal (`setFromSync`).
 */

const KEY = 'efflux_slot_sequence_v1';

/**
 * Teto de idade. Acima disto a programação provavelmente já mudou tanto que vale esperar o sync
 * (e a mídia pode nem estar mais em disco). Um dia cobre "desligou à noite, ligou de manhã".
 */
const MAX_AGE_MS = 24 * 60 * 60_000;

interface CachedSlotSequence {
  /** Quando foi gravada (epoch). */
  savedAt: number;
  /** Tier da sequência — precisa voltar junto, senão o Stream não sabe em que modo está. */
  tier: 2 | 3;
  /**
   * A época canônica (`playback_started_at`) vigente quando gravamos.
   *
   * <p>Precisa viajar junto com a sequência: é ela quem diz se o boot pode ancorar os slots no
   * relógio ou se deve começar no slot 0 como sempre. Sem isto, uma TV de
   * videowall que reinicia volta fora de fase até o primeiro sync chegar — justamente o buraco
   * que o cache existe pra fechar. Ausente (cache de versão anterior) = sem época: o boot não ancora e espera o sync.
   */
  canonicalEpoch?: string | null;
  seq: SlotSequence;
}

/** Grava a sequência ativa. Best-effort — nunca pode quebrar playback. */
export function persistSlotSequence(
  seq: SlotSequence | null,
  tier: 2 | 3 | null,
  canonicalEpoch: string | null = null,
  now: number = Date.now(),
): void {
  try {
    // Sequência vazia não serve de atalho (o boot cairia em NoContent) — e apagar aqui evitaria
    // ressuscitar uma programação que o server já retirou. Melhor não gravar e manter a anterior:
    // `setFromSync` já trata slots vazios como transiente (race de orientação/emergência).
    if (!seq || !tier || seq.slots.length === 0) return;
    const payload: CachedSlotSequence = {
      savedAt: now,
      tier,
      canonicalEpoch,
      seq,
    };
    window.localStorage?.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* cheio/indisponível — o boot só perde o atalho */
  }
}

/**
 * A decisão de aproveitar o cache, PURA (testável sem localStorage/relógio).
 *
 * <p>Cada `return false` é uma guarda obrigatória. Idade negativa = relógio da TV andou pra trás
 * (NTP); acima do teto = programação velha demais pra servir de atalho.
 */
export function isCachedSequenceUsable(
  cached: CachedSlotSequence | null,
  now: number,
): boolean {
  if (!cached) return false;
  if (!cached.seq || cached.seq.slots.length === 0) return false;
  if (cached.tier !== 2 && cached.tier !== 3) return false;
  const age = now - cached.savedAt;
  return age >= 0 && age <= MAX_AGE_MS;
}

/**
 * Lê a sequência do disco se ela ainda for utilizável AGORA. `null` = boot segue esperando o sync
 * (comportamento antigo, sempre correto).
 */
export function readUsableSlotSequence(
  now: number = Date.now(),
): { seq: SlotSequence; tier: 2 | 3; canonicalEpoch: string | null } | null {
  try {
    const raw = window.localStorage?.getItem(KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedSlotSequence;
    if (!isCachedSequenceUsable(cached, now)) return null;
    return {
      seq: cached.seq,
      tier: cached.tier,
      canonicalEpoch:
        typeof cached.canonicalEpoch === 'string'
          ? cached.canonicalEpoch
          : null,
    };
  } catch {
    return null; // JSON corrompido → ignora (nunca adivinha programação)
  }
}

/** Só pra teste / limpeza de tenant. */
export function clearSlotSequenceCache(): void {
  try {
    window.localStorage?.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export const SLOT_SEQUENCE_CACHE_KEY = KEY;
export const SLOT_SEQUENCE_MAX_AGE_MS = MAX_AGE_MS;
export type { CachedSlotSequence };
